import { getUser } from '@api';
import { AuthUser } from '@domain/types';
import { useAsyncError } from '@hooks/useAsyncError.hook';
import { LOGIN_PARTITION, WEBVIEW_INTERVAL } from '@launcher/constants';
import { useAuthenticateUser } from '@launcher/hooks/useAuthenticateUser.hook';
import { EnvManager } from '@services/environment';
import { LocalStorage } from '@services/localStorage.service';
import { logger } from '@services/logger/logger.service';
import {
  selectDesktopSsoConnectionSettings,
  selectDesktopUsernameSettings,
} from '@store/desktop/desktop.selector';
import { getIsAuthenticated } from '@store/user/user.selector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const DesktopLoginProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const throwError = useAsyncError();
  const onAuthenticateUserSuccess = useAuthenticateUser();
  const isAuthenticated = useSelector(getIsAuthenticated);

  const [webviewUrl, setWebviewUrl] = useState<string | undefined>();
  const [webviewData, setWebviewData] = useState<{ code: string; state: string }>();

  const webviewRef = useRef<HTMLWebViewElement | null>(null);
  const logCallback = (e: any) => {
    logger.info('LOGIC', `LOGIN_WEBVIEW: ${e.message}`);
  };

  const desktopUsernameSetting = useSelector(selectDesktopUsernameSettings);
  const desktopSsoConnectionSettings = useSelector(selectDesktopSsoConnectionSettings);

  /*
  Session cookies has been set, fetch user data
  */
  const fetchUser = useCallback(async () => {
    const userResponse = await getUser();
    if (userResponse.ok) {
      const user = await userResponse.json();
      onAuthenticateUserSuccess(user as unknown as AuthUser);
    } else {
      throwError(new Error('Cannot get user data'));
    }
  }, [onAuthenticateUserSuccess, throwError]);

  /*
  Fetch webview url from backend. We muste do that because url contains sensitive data that we don't want to expose in the frontend
  */
  const fetchWebviewUrl = useCallback(async () => {
    let url = `${EnvManager.getBaseUrlBackend()}/auth/alphonse_oidc_electron_redirection_cafe_beca?origin=${
      window.location.protocol
    }//${window.location.host}${window.location.pathname}&app_name=electron_redirection_cafe_beca`;

    // FORCE_LOGIN_TO_WORKSPACE_ID is set when user is switching workspace
    const forcedWorkspaceId = localStorage.getItem('FORCE_LOGIN_TO_WORKSPACE_ID');
    if (forcedWorkspaceId) {
      localStorage.removeItem('FORCE_LOGIN_TO_WORKSPACE_ID');
      url += `&workspace_id=${forcedWorkspaceId}`;
    }

    const emailFromLocalstorage = LocalStorage.get('LIFEN_USER_EMAIL');
    const email = emailFromLocalstorage || desktopUsernameSetting;

    if (email) {
      url += `&email=${encodeURIComponent(email)}`;
    }

    if (desktopSsoConnectionSettings !== '') {
      url += `&connection=${desktopSsoConnectionSettings}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throwError(new Error('Failed to fetch webview url'));
    }

    const jsonResponse = await response.json();

    if (!Object.hasOwn(jsonResponse, 'location')) {
      throwError(new Error('Webview URL : Returned object does not have a location property'));
    }

    return jsonResponse.location;
  }, [throwError]);

  /*
  Fetch session cookie from backend using given "code" and "state". When done, fetch user data
  */
  const fetchSessionCookie = useCallback(
    async (authCode: string, authState: string) => {
      const callbackUrl = `${EnvManager.getBaseUrlBackend()}/auth/alphonse_oidc_electron_redirection_cafe_beca/callback?origin=${
        window.location.protocol
      }//${window.location.host}${
        window.location.pathname
      }&app_name=electron_redirection_cafe_beca&code=${authCode}&state=${authState}`;

      logger.info('LOGIC', `Fetching session cookies.`);

      const response = await fetch(callbackUrl, {
        method: 'GET',
        credentials: 'include',
        redirect: 'manual',
      });

      // callback is a 302 redirect that we canceled due to redirect: 'manual'
      if (response.type !== 'opaqueredirect' && response.status !== 0) {
        throwError(new Error('Cannot get session cookie'));
      } else {
        fetchUser();
      }
    },
    [fetchUser, throwError],
  );

  /*
  Search for "code" and "state" in the webview. When found, fetch the session cookie
  */
  const handleWebviewAuthentication = useCallback(
    async (loginWebview: HTMLElement, interval: NodeJS.Timeout) => {
      const webviewState = await (loginWebview as any).executeJavaScript('window.state');
      const webviewCode = await (loginWebview as any).executeJavaScript('window.code');

      if (webviewState && webviewCode) {
        clearInterval(interval);

        logger.info('LOGIC', `Successfully get code and state from webview.`);

        setWebviewData({ code: webviewCode, state: webviewState });

        await fetchSessionCookie(webviewCode, webviewState);
      }
    },
    [fetchSessionCookie],
  );

  /*
  Init webivew URL that will render BECA's login page
  */
  const initWebviewUrl = useCallback(async () => {
    if (webviewUrl) {
      return;
    }

    logger.info('LOGIC', `Init webview URL`);

    const url = await fetchWebviewUrl();
    setWebviewUrl(url);
  }, [fetchWebviewUrl, webviewUrl]);

  /*
  If webview is found, register listener to see webview logs in the main console
  */
  const setWebviewRef = useCallback((node: HTMLWebViewElement) => {
    if (!webviewRef.current && !node) {
      return;
    }

    if (node && !webviewRef.current) {
      webviewRef.current = node;
    }

    const ref = webviewRef.current;

    ref?.addEventListener('console-message', logCallback);
  }, []);

  /*
  Cleanup function
  */
  useEffect(
    () => () => {
      if (webviewUrl) {
        const ref = webviewRef.current;
        ref?.removeEventListener('console-message', logCallback);
      }
    },
    [webviewUrl],
  );

  /*
  Wait for user data to be successfully loaded in the webview
  */
  useEffect(() => {
    const interval = setInterval(async () => {
      const loginWebview = document.getElementById('loginWebview');
      if (loginWebview && webviewUrl && !webviewData && !isAuthenticated) {
        await handleWebviewAuthentication(loginWebview, interval);
      }
    }, WEBVIEW_INTERVAL);

    return function cleanup() {
      clearInterval(interval);
    };
  }, [
    dispatch,
    fetchSessionCookie,
    handleWebviewAuthentication,
    isAuthenticated,
    onAuthenticateUserSuccess,
    throwError,
    webviewData,
    webviewUrl,
  ]);

  useEffect(() => {
    initWebviewUrl();
  }, [initWebviewUrl]);

  // We render login process in a webview until user has been authenticated (only on desktop), so it does not have access to Node/Electron.
  // desktop settings contains `username` and `ssoConnection` which can be passed as optional params to the login page
  // code reference:  https://github.com/honestica/lifen-core-apps/blob/93b0f0f912ed6ecb446a547387b38012d5c5b5be/packages/client/src/launcher/helpers/desktop/loginWebview.helper.ts#L32
  if (webviewUrl && !isAuthenticated && !webviewData) {
    return (
      <>
        <webview
          ref={setWebviewRef}
          partition={LOGIN_PARTITION}
          data-testid="desktop-webview"
          id="loginWebview"
          src={webviewUrl}
          style={{ position: 'absolute', width: '100%', height: '100vh' }}
          allowpopups={'true' as any} // there is a bug in some electron version we use and the setting need to be a string
        />
      </>
    );
  }

  return <>{children}</>;
};
